  // FILE: src/admin/pages/support/SupportEntityDetailLayout.jsx
  import React, { useMemo } from "react";
  import { NavLink, Outlet, useNavigate, useParams } from "react-router-dom";

  function classNames(...xs) {
    return xs.filter(Boolean).join(" ");
  }

  const BRAND = { pink: "#FF2D83", ink: "#0B0E13" };

  const ENTITY_LABEL = {
    users: "User",
    owners: "Owner",
    drivers: "Driver",
  };

  function IconChevronLeft({ className = "" }) {
    return (
      <svg viewBox="0 0 24 24" className={className} fill="none" aria-hidden="true">
        <path d="M14 7l-5 5 5 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    );
  }

  function IconAudit({ className = "" }) {
    return (
      <svg viewBox="0 0 24 24" className={className} fill="none" aria-hidden="true">
        <path
          d="M9 11h6M9 15h6M8 3h8l3 3v15a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2Z"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinejoin="round"
        />
        <path d="M16 3v4h4" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
      </svg>
    );
  }

  export default function SupportEntityDetailLayout() {
    const { entityType = "users", entityId } = useParams();
    const navigate = useNavigate();
    const label = ENTITY_LABEL[entityType] || "User";

    const isOwners = entityType === "owners";

    const tabs = useMemo(() => {
      const base = `/admin/support/${entityType}/${entityId}`;

      if (entityType === "owners") {
        return [
          { label: "Overview", to: `${base}/overview` },
          { label: "Vehicles", to: `${base}/vehicles` },
          { label: "Inbox", to: `${base}/inbox` },
          { label: "Bookings", to: `${base}/bookings` },
          { label: "Rides", to: `${base}/rides` }, // ✅ keep
          { label: "Payouts", to: `${base}/payouts` },
          { label: "Notes", to: `${base}/notes` },
        ];
      }

      if (entityType === "drivers") {
        return [
          { label: "Overview", to: `${base}/overview` },
          { label: "Bookings", to: `${base}/bookings` },
          { label: "Notes", to: `${base}/notes` },
        ];
      }

      // users default (existing)
      return [
        { label: "Overview", to: `${base}/overview` },
        { label: "Requests", to: `${base}/requests` },
        { label: "Bookings", to: `${base}/bookings` },
        { label: "Payments", to: `${base}/payments` },
        { label: "Notes", to: `${base}/notes` },
      ];
    }, [entityType, entityId]);

    return (
      <div className="min-w-0">
        {/* Header */}
        <div className="mb-4 rounded-2xl border border-slate-200 bg-white">
          {/* ✅ Owners: match User Support header style (clean / no extra chips) */}
          {isOwners ? (
            <div className="flex flex-col gap-3 px-5 py-4 lg:flex-row lg:items-center lg:justify-between">
              <div className="min-w-0">
                <div className="flex items-center gap-3">
                  <span className="h-2 w-2 shrink-0" style={{ background: BRAND.pink, borderRadius: 3 }} aria-hidden="true" />
                  <div className="min-w-0">
                    <div className="truncate text-[18px] font-extrabold tracking-tight text-slate-900">
                      {label} Support <span className="text-slate-400">•</span> ID {entityId}
                    </div>
                    {/* ❌ no subtitle for owners */}
                  </div>
                </div>
              </div>

              <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-end">
                <button
                  onClick={() => navigate(`/admin/support/${entityType}`)}
                  className="inline-flex h-10 items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-4 text-sm font-semibold text-slate-900 hover:bg-slate-50"
                >
                  <IconChevronLeft className="h-4 w-4 text-slate-700" />
                  Back to search
                </button>

                <button
                  onClick={() =>
                    navigate(
                      `/admin/audit?entityType=${encodeURIComponent(entityType)}&entityId=${encodeURIComponent(entityId)}`
                    )
                  }
                  className="inline-flex h-10 items-center justify-center gap-2 rounded-xl border border-slate-900 bg-slate-900 px-4 text-sm font-semibold text-white hover:opacity-95"
                >
                  <IconAudit className="h-4 w-4 text-white" />
                  View audit
                </button>
              </div>
            </div>
          ) : (
            /* ✅ Users + Drivers: keep your current header unchanged */
            <div className="flex flex-col gap-3 px-5 py-4 lg:flex-row lg:items-center lg:justify-between">
              <div className="min-w-0">
                <div className="flex items-center gap-3">
                  <span className="h-2 w-2 shrink-0" style={{ background: BRAND.pink, borderRadius: 3 }} aria-hidden="true" />
                  <div className="min-w-0">
                    <div className="truncate text-[18px] font-extrabold tracking-tight text-slate-900">
                      {label} Support <span className="text-slate-400">•</span> ID {entityId}
                    </div>
                    <div className="mt-1 text-[13px] font-medium text-slate-500">
                      Review activity and manage support actions for this {label.toLowerCase()}.
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-end">
                <button
                  onClick={() => navigate(`/admin/support/${entityType}`)}
                  className="inline-flex h-10 items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-4 text-sm font-semibold text-slate-900 hover:bg-slate-50"
                >
                  <IconChevronLeft className="h-4 w-4 text-slate-700" />
                  Back to search
                </button>

                <button
                  onClick={() =>
                    navigate(
                      `/admin/audit?entityType=${encodeURIComponent(entityType)}&entityId=${encodeURIComponent(entityId)}`
                    )
                  }
                  className="inline-flex h-10 items-center justify-center gap-2 rounded-xl border border-slate-900 bg-slate-900 px-4 text-sm font-semibold text-white hover:opacity-95"
                >
                  <IconAudit className="h-4 w-4 text-white" />
                  View audit
                </button>
              </div>
            </div>
          )}

          {/* Tabs */}
          <div className="border-t border-slate-200 px-3 py-3">
            <div className="-mx-3 overflow-x-auto px-3">
              <div className="flex w-max min-w-full gap-2">
              {tabs.map((t) => (
                <NavLink
                  key={t.to}
                  to={t.to}
                  className={({ isActive }) =>
                    classNames(
                      "inline-flex h-10 items-center rounded-xl border px-4 text-sm font-semibold transition",
                      isActive
                        ? "border-slate-900 bg-slate-900 text-white"
                        : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
                    )
                  }
                >
                  {t.label}
                </NavLink>
              ))}
              </div>
            </div>
          </div>
        </div>

        <Outlet />
      </div>
    );
  }
